import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { BillingCountry } from '../../shared/models/billing-country.model';
import { of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import {
  AdminUserUpdateResult,
  PaymentType,
  Role,
  UserFormData,
  ZohoCustomerSettings,
  refreshZohoCustomerMapping,
} from '../../shared/models/user.model';
import { BillingCountryService } from '../../shared/services/billing-country.service';
import { DialogRef, ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UsersService } from '../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../shared/services/validation.service';
import {
  canManageInvoiceRecipients,
  formatInvoiceRecipientEmails,
  invoiceRecipientEmailsValidator,
  normalizeInvoiceRecipientEmails,
  registerInvoiceRecipientValidationMessages,
  splitInvoiceRecipientEmails,
} from '../invoice-recipients';

@Component({
  standalone: false,
  selector: 'app-users-form',
  styleUrls: ['./users-form.component.scss'],
  templateUrl: 'users-form.component.html',
})
export class UsersFormComponent {
  @ViewChild('userListModalTpl', { read: TemplateRef, static: false }) userListModalTpl: any;
  @ViewChild('verifyCompanyDetailsTpl', { read: TemplateRef, static: false }) verifyCompanyDetailsTpl: any;

  selectUserMode;
  parentUser;
  ownerUser;
  salesRepresentativeUser;
  convertedBy;
  convertedByUser;
  messageForModal: string = null;

  loading = false;
  @Input() model;
  @Output() onAfterSave = new EventEmitter<void | boolean | 'reload'>();

  countries: BillingCountry[] = [];

  countSubaccounts: number;
  convertedToSubaccount = false;

  types;
  roles;

  backButtonShow = true;
  invoiceEmails = '';
  invoiceRecipientsLoaded = true;
  zohoSettings: ZohoCustomerSettings = null;
  zohoSettingsLoaded = false;
  zohoSettingsLoading = false;
  zohoSyncWarning: string = null;
  vatValidationLoading = false;

  form: FormGroup;
  userListModal: DialogRef;

  private submitSection: 'profile' | 'company' | 'platform' | 'legalContacts' | 'internal' | null = null;
  savedModelSnapshot: UserFormData = null;
  private savedZohoSettings: ZohoCustomerSettings = null;

  parentIdValidator: ValidatorFn = control => {
    if (this.model?.userRole === Role.SUB && !control?.value) {
      return { parentIdValidator: true };
    }
    return null;
  };

  getVatNumberRequired() {
    return this.form.get('vatNumber').hasValidator(Validators.required);
  }

  get vatValidationIndicator(): 'verified' | 'failed' | null {
    if (!this.vatValidationRequired || !this.model?.vatNumber) {
      return null;
    }
    if (this.model.vatValidationStatus === 'VERIFIED') {
      return 'verified';
    }
    return this.model.vatValidationStatus === 'NOT_REQUIRED' ? null : 'failed';
  }

  get vatValidationTooltip(): string | null {
    if (!this.vatValidationRequired) {
      return null;
    }
    switch (this.model?.vatValidationStatus) {
      case 'VERIFIED':
        return 'VAT verified';
      case 'INVALID':
        return 'VAT verification failed';
      case 'UNAVAILABLE':
        return 'VAT verification is temporarily unavailable';
      case 'UNVERIFIED':
        return 'VAT not verified';
      default:
        return this.model?.vatNumber ? 'VAT not verified' : null;
    }
  }

  get showZohoContactLink(): boolean {
    return this.service.authUser?.role === Role.ADMIN && !!(this.zohoSettings?.zohoContactId || this.model?.zohoContactId);
  }

  get showZohoCustomerSettings(): boolean {
    return this.service?.authUser?.role === Role.ADMIN && this.model?.userRole === Role.MAIN && !!this.model?.id;
  }

  get zohoContactUrl(): string | null {
    return this.showZohoContactLink
      ? `https://books.zoho.com/app/${environment.zohoBooksOrganizationId}#/contacts/${encodeURIComponent(
          this.zohoSettings?.zohoContactId || this.model.zohoContactId
        )}`
      : null;
  }

  constructor(
    public notificationService: NotificationService,
    public formBuilder: FormBuilder,
    public service: UsersService,
    public billingCountryService: BillingCountryService,
    public validationService: ValidationService,
    public modal: ModalService
  ) {
    this.model = service.create();
    billingCountryService.all().subscribe({
      next: countries => {
        this.countries = [...countries].sort((a, b) => a.name.localeCompare(b.name));
      },
    });

    const passwordValidator = v => {
      if (!this.model.id && !v.value) {
        return { required: true };
      }
      return null;
    };
    const rankingIndexValidator = c => {
      if (!this.model.userRole || this.model.userRole === Role.SUB) {
        return null;
      }
      const val = parseInt(c.value);
      if (isNaN(val)) {
        return { integer: true };
      }
      if (val < 0) {
        return { min: { min: 0 } };
      }
      if (val > 10) {
        return { max: { max: 10 } };
      }
      return null;
    };
    validationService.addFormatter('parentIdValidator', validationService.createTextFormatter('Select parent'));

    this.form = formBuilder.group({
      fullName: ['', Vld.compose([Vld.required, Vld.minLength(3), Vld.maxLength(255)])],
      email: ['', Vld.compose([Vld.required, Vld.email(true)])],
      password: ['', Vld.compose([passwordValidator, Vld.maxLength(50), Vld.password(true)]), [Vld.passwordBackend(service)]],
      paymentType: [''],
      enabled: [''],
      emailVerified: [''],
      sendVerificationEmail: [''],
      username: ['', Vld.compose([Vld.required, Vld.username(true)])],
      companyName: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(255)])],
      companyDetailsVerified: [{ value: false, disabled: true }],
      departmentName: ['', Vld.compose([Vld.minLength(1), Vld.maxLength(255)])],
      websiteUrl: ['', Vld.compose([Vld.noSpace()])],
      countryCode: ['', Vld.compose([Vld.required])],
      userRole: [''],
      phone: ['', Vld.compose([Vld.required, Vld.globalPhoneNumber(), Vld.noSpace()])],
      emailForAlert: ['', Vld.compose([Vld.email(true)])],
      invoiceEmails: ['', invoiceRecipientEmailsValidator()],
      dataProtectionOfficerEmail: ['', Vld.compose([Vld.required, Vld.email(true)])],
      legalRepresentativeEmail: ['', Vld.compose([Vld.email(true)])],
      procurementEmail: ['', Vld.compose([Vld.email(true)])],
      addressCity: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(100)])],
      addressStreet: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(255)])],
      addressState: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(100)])],
      addressZip: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(20)])],
      vatNumber: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(20)])],
      vatExempt: [false],
      testRetriesEnabled: [''],
      rankingIndex: ['', rankingIndexValidator],
      parentId: ['', Vld.compose([this.parentIdValidator])],
      ownerId: [''],
      testTtl: ['', UsersService.getValidatorForTestTtlMin()],
      minBalance: [1, Vld.compose([Vld.digits(), Vld.noSpace(), Vld.min(0)])],
      source: [''],
      convertedBy: [''],
      salesRepresentative: [''],
      ipWhitelistEnabled: [''],
      ipWhitelistAddressList: [''],
      comment: ['', Vld.compose([Vld.maxLength(255)])],
    });
    registerInvoiceRecipientValidationMessages(validationService);
    this.form.statusChanges.subscribe(_ => {
      if (this.model.emailVerified) {
        this.model.sendVerificationEmail = false;
      }
    });
    this.types = this.service.getTypes();
    this.roles = this.service.getCreateRoles();
  }

  // Section-apply buttons set submitSection via dedicated apply methods below

  onChangeModelOnRole(model) {
    if (model.userRole === Role.SUB) {
      const keysTobeNull = [
        'rankingIndex',
        'vatNumber',
        'vatExempt',
        'testRetriesEnabled',
        'testTtl',
        'websiteUrl',
        'comment',
        'dataProtectionOfficerEmail',
        'legalRepresentativeEmail',
        'procurementEmail',
      ];
      keysTobeNull.map(key => {
        model[key] = null;
      });
    }
    return model;
  }

  onSubmit() {
    if (this.loading) return;
    this.loading = true;
    const isCreate = !this.model?.id;

    let payload: UserFormData = !isCreate && this.submitSection ? this.buildSectionPayload(this.submitSection) : this.model;
    payload = this.onChangeModelOnRole(Object.assign({}, payload));
    // Extra safety: when converting to subaccount, ensure a parent is selected
    if (this.model.id && this.convertedToSubaccount) {
      if (!this.model.parent || !this.form.get('parentId')?.value) {
        this.loading = false;
        const parentCtrl = this.form.get('parentId');
        parentCtrl?.markAsTouched();
        parentCtrl?.updateValueAndValidity();
        this.notificationService.error('Please select a parent account', 'Users');
        return;
      }
    }
    if (this.model.id) {
      if (this.convertedToSubaccount) {
        this.service.convertToSubaccount(this.model.id, this.model.parent.id).subscribe({
          next: () => {
            if (!this.model.source) {
              this.model.source = null;
            }
            payload.parent = this.model.parent;
            this.service.updateByAdmin(payload).subscribe({
              next: result => {
                this.afterSuccessfulUpdate();
                this.handleAdminUpdateResult(result);
              },
              error: e => this.handleSaveError(e),
            });
          },
          error: e => this.handleSaveError(e),
        });
      } else {
        const shouldUpdateZohoSettings =
          this.submitSection === 'company' && this.showZohoCustomerSettings && this.zohoSettingsLoaded && this.hasZohoSettingsChanges();
        const updateRequest = shouldUpdateZohoSettings
          ? this.updateZohoSettings().pipe(
              switchMap(settings =>
                this.service
                  .updateByAdmin(payload)
                  .pipe(
                    map(result =>
                      settings.zohoSyncWarning && !result.warning
                        ? { ...result, zohoSyncStatus: 'FAILED' as const, warning: settings.zohoSyncWarning }
                        : result
                    )
                  )
              )
            )
          : this.service.updateByAdmin(payload);
        const shouldSyncInvoiceRecipients =
          this.canEditInvoiceRecipients() && this.invoiceRecipientsLoaded && (this.submitSection === 'platform' || this.submitSection === null);
        const request = shouldSyncInvoiceRecipients
          ? updateRequest.pipe(
              switchMap(updateResult => {
                const emails = splitInvoiceRecipientEmails(this.invoiceEmails);
                return this.service.updateInvoiceRecipientsByAdmin(this.model.id, { emails }).pipe(map(() => updateResult));
              })
            )
          : updateRequest;
        request.subscribe({
          next: result => {
            if (shouldSyncInvoiceRecipients) {
              this.invoiceEmails = normalizeInvoiceRecipientEmails(this.invoiceEmails);
              this.form.get('invoiceEmails')?.patchValue(this.invoiceEmails);
            }
            this.afterSuccessfulUpdate();
            this.handleAdminUpdateResult(result);
          },
          error: e => this.handleSaveError(e),
        });
      }
    } else {
      this.service
        .createByAdmin(payload)
        .pipe(
          switchMap((userId: number) => {
            const emails = splitInvoiceRecipientEmails(this.invoiceEmails);
            if (!canManageInvoiceRecipients(this.model.userRole) || emails.length === 0) {
              return of(null);
            }
            return this.service.updateInvoiceRecipientsByAdmin(userId, { emails });
          })
        )
        .subscribe({
          next: () => this.handleSaveSuccess(),
          error: e => this.handleSaveError(e),
        });
    }
  }

  showUserListModal(mode) {
    this.selectUserMode = mode;
    this.userListModal = this.modal.alert().component(this.userListModalTpl).open();
  }

  onSelectParentUser(user) {
    if (this.selectUserMode === 'parent') {
      if (this.model.id === user.id) {
        this.notificationService.error('User and parent must have different ID', 'Users');
        return;
      }
      this.parentUser = user;
      this.model.parent = user;
      const label = `${user.email} <${user.username}>`;
      this.form.controls.parentId.patchValue(label);
      this.reloadCompanyDetails();
    }

    if (this.selectUserMode === 'owner') {
      this.ownerUser = user;
      this.model.owner = user;
      const label = `${user.email} <${user.username}>`;
      this.form.controls.ownerId.patchValue(label);
    }

    if (this.selectUserMode === 'salesRepresentative') {
      this.salesRepresentativeUser = user;
      this.model.salesRepresentative = user;
      const label = `${user.email} <${user.username}>`;
      this.form.controls.salesRepresentative.patchValue(label);
    }

    if (this.selectUserMode === 'convertedBy') {
      this.convertedByUser = user;
      this.model.convertedBy = user;
      const label = `${user.email} <${user.username}>`;
      this.form.controls.convertedBy.patchValue(label);
    }

    if (this.userListModal) {
      this.userListModal.close(true);
    }
  }

  onChangeUserRole() {
    const role = this.model.userRole;
    if (role !== Role.MAIN) {
      this.resetVatExemption();
    }
    if ((role === Role.ADMIN || role === Role.MAIN) && this.model.parent) {
      this.model.parent = null;
      this.parentUser = null;
      this.form.controls.parentId.patchValue('');
    }

    if (role === Role.ADMIN || role === Role.SUB || role === Role.DEPUTY) {
      this.model.owner = null;
      this.form.controls.ownerId.patchValue('');
    }

    if (role !== Role.MAIN || role === Role.SUB || role === Role.DEPUTY) {
      this.model.source = null;
      this.form.controls.source.patchValue('');
      this.model.convertedBy = null;
      this.form.controls.convertedBy.patchValue('');
      this.model.salesRepresentative = null;
      this.form.controls.salesRepresentative.patchValue('');
    }
  }

  onClickConvertToSubaccount() {
    if (!this.form.contains('parentId')) {
      this.form.addControl('parentId', this.formBuilder.control('', Vld.compose([this.parentIdValidator])));
    }
    this.model.userRole = Role.SUB;
    this.convertedToSubaccount = true;
    this.onChangeUserRole();
    this.form.get('parentId')?.updateValueAndValidity();
    this.disableAndRemoverFieldsForSubaccount();
  }

  showParent(): boolean {
    return this.model.userRole === Role.SUB || this.model.userRole === Role.DEPUTY;
  }

  showOwner(): boolean {
    return this.model.userRole === Role.MAIN;
  }

  showVatNumber(): boolean {
    return this.model.userRole !== Role.SUB && this.model.userRole !== Role.DEPUTY;
  }

  showVatExemption(): boolean {
    return !!this.model?.id && this.model.userRole === Role.MAIN && this.model.countryCode === 'DE';
  }

  onVatIdentityChanged(): void {
    this.model.vatValidationStatus = this.vatValidationRequired ? 'UNVERIFIED' : 'NOT_REQUIRED';
    this.resetVatExemption();
  }

  showRankingIndex(): boolean {
    return this.model.userRole !== Role.SUB && this.model.userRole !== Role.DEPUTY;
  }

  showBackupTests(): boolean {
    return this.model.userRole !== Role.SUB && this.model.userRole !== Role.DEPUTY;
  }

  showTestTtl(): boolean {
    return this.model.userRole !== Role.SUB && this.model.userRole !== Role.DEPUTY;
  }

  showComment(): boolean {
    return this.model.userRole !== Role.SUB && this.model.userRole !== Role.DEPUTY;
  }

  showInternalInformationApplyChanges(): boolean {
    if (this.model.userRole === Role.SUB) {
      return !(this.form.get('parentId').disabled && this.form.get('salesRepresentative').disabled);
    }
    return true;
  }

  isFormValid(): boolean {
    if ((this.form.valid && this.model.userRole === Role.SUB) || this.model.userRole === Role.DEPUTY) {
      if (this.model.parent) {
        return true;
      }
      return false;
    }
    return this.form.valid;
  }

  isConvertToSubaccount(): boolean {
    return this.model && this.model.id && this.model.userRole === Role.MAIN && this.countSubaccounts === 0;
  }

  reloadCompanyDetails(): void {
    if (this.model.parent) {
      this.loading = true;
      const parentId = this.model.parent.id;
      this.service.one(parentId).subscribe((parentModel: UserFormData) => {
        if (this.model.parent?.id !== parentId) return;
        this.parentUser = parentModel;
        this.setCompanyDetails(parentModel);
        this.salesRepresentativeUser = parentModel.salesRepresentative;
        this.ownerUser = parentModel.owner;
        this.model.owner = parentModel.owner;
        const ownerLabel = parentModel.owner ? `${parentModel.owner.email} <${parentModel.owner.username}>` : '';
        this.form.controls.ownerId.patchValue(ownerLabel);
        this.loading = false;
      });
    }
  }

  setModel(model: UserFormData, parent: UserFormData = null): void {
    this.model = model;
    this.model.vatExempt = model.vatExempt === true;
    if (this.model.paymentType === PaymentType.POSTPAID) {
      this.form.removeControl('minBalance');
    }
    if (this.model.userRole === Role.MAIN) {
      this.form.removeControl('parentId');
      this.service.getUserDetailsFromEmail(this.model.email).subscribe(detail => {
        this.countSubaccounts = detail.countSubaccounts ?? 0;
        this.model.companyDetailsVerified = detail.companyDetailsVerified ?? false;
      });
    }
    if (this.model.userRole === Role.SUB) {
      this.disableAndRemoverFieldsForSubaccount();
    }
    if (model.parent) {
      this.parentUser = parent;
      this.ownerUser = parent.owner;
      this.form.get('ownerId').setValue(parent.owner ? parent.owner.email + ' <' + parent.owner.username + '>' : '');
      this.form.get('parentId').setValue(parent ? parent.email + ' <' + parent.username + '>' : '');
      this.form.get('ownerId').disable();
      this.form.get('parentId').disable();
    }
    if (model.owner && !this.ownerUser) {
      this.ownerUser = model.owner;
    }
    if (model.salesRepresentative && !this.salesRepresentativeUser) {
      this.salesRepresentativeUser = model.salesRepresentative;
    }
    if (model.convertedBy && !this.convertedByUser) {
      this.convertedByUser = model.convertedBy;
    }
    this.captureSavedSnapshot();
    if (this.model.id && this.canEditInvoiceRecipients()) {
      this.loadInvoiceRecipients(this.model.id);
    } else if (!this.model.id) {
      this.invoiceRecipientsLoaded = true;
    }
    if (this.showZohoCustomerSettings) {
      this.loadZohoCustomerSettings(this.model.id);
    }
  }

  private loadZohoCustomerSettings(userId: number): void {
    this.zohoSettingsLoaded = false;
    this.zohoSettingsLoading = true;
    this.service
      .getZohoCustomerSettings(userId)
      .pipe(finalize(() => (this.zohoSettingsLoading = false)))
      .subscribe({
        next: settings => {
          this.zohoSettings = settings;
          this.savedZohoSettings = { ...settings };
          this.zohoSettingsLoaded = true;
        },
        error: error => this.validationService.handleRequestError(error, 'Users'),
      });
  }

  private refreshZohoContactId(userId: number, onComplete: () => void = () => (this.loading = false)): void {
    this.service
      .getZohoCustomerSettings(userId)
      .pipe(finalize(onComplete))
      .subscribe({
        next: backendSettings => {
          const zohoContactId = backendSettings.zohoContactId;
          this.zohoSettings = refreshZohoCustomerMapping(this.zohoSettings, backendSettings);
          this.savedZohoSettings = refreshZohoCustomerMapping(this.savedZohoSettings, backendSettings);
          this.model.zohoContactId = zohoContactId;
          this.zohoSettingsLoaded = true;
        },
        error: error => this.validationService.handleRequestError(error, 'Users'),
      });
  }

  private updateZohoSettings() {
    return this.service
      .updateZohoCustomerSettings(this.model.id, {
        zohoSyncEnabled: this.zohoSettings.zohoSyncEnabled,
        paymentTermsDays: this.zohoSettings.paymentTermsDays,
      })
      .pipe(
        map(settings => {
          this.zohoSettings = settings;
          this.savedZohoSettings = { ...settings };
          this.model.zohoContactId = settings.zohoContactId;
          return settings;
        })
      );
  }

  private hasZohoSettingsChanges(): boolean {
    return (
      !!this.zohoSettings &&
      !!this.savedZohoSettings &&
      (this.zohoSettings.zohoSyncEnabled !== this.savedZohoSettings.zohoSyncEnabled ||
        this.zohoSettings.paymentTermsDays !== this.savedZohoSettings.paymentTermsDays)
    );
  }

  get canValidateVatManually(): boolean {
    return (
      this.service.authUser?.role === Role.ADMIN &&
      this.model?.userRole === Role.MAIN &&
      !!this.model?.id &&
      !!this.model?.vatNumber?.trim() &&
      this.vatValidationRequired &&
      this.model.vatValidationStatus !== 'VERIFIED' &&
      !this.hasUnsavedVatIdentityChanges
    );
  }

  private get hasUnsavedVatIdentityChanges(): boolean {
    if (!this.savedModelSnapshot) {
      return true;
    }
    return this.model?.vatNumber !== this.savedModelSnapshot.vatNumber || this.model?.countryCode !== this.savedModelSnapshot.countryCode;
  }

  get vatValidationRequired(): boolean {
    return this.countries.find(country => country.code === this.model?.countryCode)?.euVatArea === true;
  }

  validateVatManually(): void {
    if (!this.canValidateVatManually || this.vatValidationLoading) return;
    this.vatValidationLoading = true;
    this.service.validateVatByAdmin(this.model.id).subscribe({
      next: result => {
        this.model.vatValidationStatus = result.vatValidationStatus;
        if (result.vatValidationStatus !== 'UNAVAILABLE') {
          if (result.vatNumber != null) {
            this.model.vatNumber = result.vatNumber;
            this.form.get('vatNumber')?.setValue(result.vatNumber, { emitEvent: false });
          }
          if (this.savedModelSnapshot) {
            this.savedModelSnapshot.vatNumber = this.model.vatNumber;
            this.savedModelSnapshot.vatValidationStatus = result.vatValidationStatus;
          }
        }
        this.vatValidationLoading = false;
        if (result.vatValidationStatus === 'VERIFIED') {
          this.notificationService.success('VAT verified', 'Users');
        } else if (result.vatValidationStatus === 'NOT_REQUIRED') {
          this.notificationService.success('VAT validation is not required for this country', 'Users');
        } else {
          this.notificationService.warning('VAT could not be verified', 'Users');
        }
      },
      error: error => {
        this.vatValidationLoading = false;
        this.validationService.handleRequestError(error, 'Users');
      },
    });
  }

  paymentTermsLabel(days: number): string {
    return `Net ${days}`;
  }

  private loadInvoiceRecipients(userId: number): void {
    this.invoiceRecipientsLoaded = false;
    this.service.getInvoiceRecipientsByAdmin(userId).subscribe({
      next: response => {
        this.invoiceEmails = formatInvoiceRecipientEmails(response?.emails);
        this.form.get('invoiceEmails')?.patchValue(this.invoiceEmails);
        this.invoiceRecipientsLoaded = true;
      },
      error: error => {
        this.validationService.handleRequestError(error, 'Users');
      },
    });
  }

  canEditInvoiceRecipients(): boolean {
    return canManageInvoiceRecipients(this.model?.userRole);
  }

  disableAndRemoverFieldsForSubaccount(): void {
    this.form.get('companyName').disable();
    this.form.get('websiteUrl').disable();
    this.form.get('vatNumber').disable();
    this.form.get('countryCode').disable();
    this.form.get('vatExempt').disable();
    this.form.get('addressCity').disable();
    this.form.get('addressZip').disable();
    this.form.get('addressStreet').disable();
    this.form.get('addressState').disable();
    this.form.get('departmentName').disable();
    this.form.get('dataProtectionOfficerEmail').disable();
    this.form.get('legalRepresentativeEmail').disable();
    this.form.get('procurementEmail').disable();
    this.form.get('salesRepresentative').disable();

    this.form.removeControl('invoiceEmails');
    this.form.removeControl('emailForAlert');
    this.form.removeControl('testTtl');
    this.form.removeControl('password');
    this.form.removeControl('minBalance');
  }

  private setCompanyDetails(model: UserFormData) {
    this.model.companyName = model.companyName;
    this.model.websiteUrl = model.websiteUrl;
    this.model.vatNumber = model.vatNumber;
    this.model.vatExempt = model.vatExempt === true;
    this.model.countryName = model.countryName;
    this.model.countryCode = model.countryCode;
    this.model.addressCity = model.addressCity;
    this.model.addressZip = model.addressZip;
    this.model.addressStreet = model.addressStreet;
    this.model.addressState = model.addressState;
    this.model.departmentName = model.departmentName;
    this.model.dataProtectionOfficerEmail = model.dataProtectionOfficerEmail;
    this.model.legalRepresentativeEmail = model.legalRepresentativeEmail;
    this.model.procurementEmail = model.procurementEmail;
  }

  onCountryChange(countryCode: string): void {
    this.model.countryCode = countryCode;
    this.model.countryName = this.countries.find(country => country.code === countryCode)?.name ?? '';
  }

  onCountryEdited(countryCode: string): void {
    this.onCountryChange(countryCode);
    this.onVatIdentityChanged();
  }

  areCompanyFieldsValid(): boolean {
    const companyFields = [
      'companyName',
      'websiteUrl',
      'vatNumber',
      'countryCode',
      'addressCity',
      'addressZip',
      'addressStreet',
      'addressState',
      'vatExempt',
      'departmentName',
    ];
    return companyFields.every(field => this.form.get(field).valid);
  }

  verifyCompanyDetails(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.loading || (this.showZohoCustomerSettings && this.zohoSettingsLoading)) return;
    this.loading = true;
    this.messageForModal = `You are going to mark company details for this account as verified.`;
    const dialogRef = this.modal.alert().component(this.verifyCompanyDetailsTpl).open();
    dialogRef.result.then(result => {
      if (result) {
        this.service.verifyCompanyDetails(this.model.id).subscribe({
          next: () => {
            this.model.companyDetailsVerified = true;
            if (this.showZohoCustomerSettings) {
              this.refreshZohoContactId(this.model.id);
            } else {
              this.loading = false;
            }
          },
          error: e => {
            this.notificationService.error({
              title: 'Users',
              message: 'An error occurred while verifying company details',
              serviceName: 'API GATEWAY',
              requestMessage: e?.statusText,
              requestCode: e?.status,
              ts: e?.timestamp ? e.timestamp : null,
            });
            this.loading = false;
          },
        });
      } else {
        this.loading = false;
      }
    });
  }

  onCancel(): void {
    this.onAfterSave.emit(true);
  }

  // Unified success/error helpers
  private handleSaveSuccess() {
    this.loading = false;
    this.notificationService.success('User ' + (this.model.id ? 'updated' : 'created'), 'Users');
    this.onAfterSave.emit();
  }

  private handleAdminUpdateResult(result: AdminUserUpdateResult): void {
    if (result?.vatValidationStatus) {
      this.model.vatValidationStatus = result.vatValidationStatus;
    }

    if (result?.zohoSyncStatus !== 'FAILED' && !result?.warning) {
      this.zohoSyncWarning = null;
      if (this.showZohoCustomerSettings && result?.zohoSyncStatus === 'SYNCED')
        this.refreshZohoContactId(this.model.id, this.handleSaveSuccess.bind(this));
      else this.handleSaveSuccess();
      return;
    }

    this.loading = false;
    this.zohoSyncWarning = result.warning || 'Changes were saved in TelQ, but Zoho synchronization failed. Please retry the update.';
    this.notificationService.warning(this.zohoSyncWarning, 'Users');
    this.onAfterSave.emit('reload');
  }

  private handleSaveError(e: any) {
    this.loading = false;
    const fallbackMessage = 'An error occurred while ' + (this.model.id ? 'updating' : 'creating') + ' the user';
    const responseMessage = typeof e?.error === 'string' ? e.error : e?.error?.message;
    this.notificationService.error({
      title: 'Users',
      message: responseMessage || fallbackMessage,
      serviceName: 'API GATEWAY',
      requestMessage: e?.statusText,
      requestCode: e?.status,
      ts: e?.timestamp ? e.timestamp : null,
    });
  }

  private captureSavedSnapshot() {
    try {
      // Deep clone to avoid accidental mutation through references
      this.savedModelSnapshot = JSON.parse(JSON.stringify(this.model));
    } catch (e) {
      // Fallback to shallow clone if needed
      this.savedModelSnapshot = Object.assign({}, this.model);
    }
  }

  private afterSuccessfulUpdate() {
    if (this.submitSection) {
      const overrides = this.getSectionOverrides(this.submitSection);
      this.savedModelSnapshot = Object.assign({}, this.savedModelSnapshot, overrides, {
        userRole: this.model.userRole,
      });
    } else {
      this.captureSavedSnapshot();
    }
    this.submitSection = null;
  }

  private buildSectionPayload(section: 'profile' | 'company' | 'platform' | 'legalContacts' | 'internal'): UserFormData {
    const base: UserFormData = this.savedModelSnapshot
      ? (JSON.parse(JSON.stringify(this.savedModelSnapshot)) as UserFormData)
      : (JSON.parse(JSON.stringify(this.model)) as UserFormData);
    const overrides = this.getSectionOverrides(section);

    const merged: UserFormData = Object.assign({}, base, overrides, { userRole: this.model.userRole });
    return merged;
  }

  private getSectionOverrides(section: 'profile' | 'company' | 'platform' | 'legalContacts' | 'internal'): Partial<UserFormData> {
    const m = this.model as UserFormData;
    switch (section) {
      case 'profile':
        return {
          fullName: m.fullName,
          username: m.username,
          phone: m.phone,
          email: m.email,
          emailVerified: m.emailVerified,
        };
      case 'company':
        return {
          companyName: m.companyName,
          websiteUrl: m.websiteUrl,
          countryName: m.countryName,
          countryCode: m.countryCode,
          addressCity: m.addressCity,
          addressZip: m.addressZip,
          addressStreet: m.addressStreet,
          addressState: m.addressState,
          vatNumber: m.vatNumber,
          vatExempt: m.vatExempt,
          departmentName: m.departmentName,
        };
      case 'platform':
        return {
          testTtl: m.testTtl,
          emailForAlert: m.emailForAlert,
          minBalance: m.minBalance,
          password: m.password,
          testRetriesEnabled: m.testRetriesEnabled,
        };
      case 'legalContacts':
        return {
          dataProtectionOfficerEmail: m.dataProtectionOfficerEmail,
          legalRepresentativeEmail: m.legalRepresentativeEmail,
          procurementEmail: m.procurementEmail,
        };
      case 'internal':
        return {
          parent: m.parent,
          owner: m.owner,
          salesRepresentative: m.salesRepresentative,
          convertedBy: m.convertedBy,
          source: m.source,
          rankingIndex: m.rankingIndex,
          paymentType: m.paymentType,
          comment: m.comment,
        };
    }
  }

  private areControlsValid(controlNames: string[]): boolean {
    return controlNames.every(name => {
      const ctrl = this.form.get(name);
      return !ctrl || ctrl.disabled || ctrl.valid;
    });
  }

  isProfileSectionValid(): boolean {
    return this.areControlsValid(['fullName', 'username', 'phone', 'email', 'emailVerified']);
  }

  isCompanySectionValid(): boolean {
    return this.areControlsValid([
      'companyName',
      'websiteUrl',
      'countryCode',
      'addressCity',
      'addressZip',
      'addressStreet',
      'addressState',
      'vatNumber',
      'vatExempt',
      'departmentName',
    ]);
  }

  isPlatformSectionValid(): boolean {
    const controls = ['testTtl', 'emailForAlert', 'minBalance', 'password', 'testRetriesEnabled'];
    if (!this.canEditInvoiceRecipients() || this.invoiceRecipientsLoaded) {
      controls.push('invoiceEmails');
    }
    return this.areControlsValid(controls.filter(name => !!this.form.get(name)));
  }

  isLegalContactsSectionValid(): boolean {
    return this.areControlsValid(['dataProtectionOfficerEmail', 'legalRepresentativeEmail', 'procurementEmail']);
  }

  isInternalSectionValid(): boolean {
    const internal = ['parentId', 'source', 'rankingIndex', 'paymentType', 'comment', 'salesRepresentative', 'convertedBy'];
    return this.areControlsValid(internal.filter(n => !!this.form.get(n)));
  }

  private markControlsAsTouched(controlNames: string[]) {
    controlNames.forEach(name => {
      const ctrl = this.form.get(name);
      if (ctrl) {
        ctrl.markAsTouched();
        ctrl.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  applyProfileChanges() {
    if (!this.model?.id) {
      this.onSubmit();
      return;
    }
    if (!this.isProfileSectionValid()) {
      this.markControlsAsTouched(['fullName', 'username', 'phone', 'email']);
      return;
    }
    this.submitSection = 'profile';
    this.onSubmit();
  }

  applyCompanyChanges() {
    if (!this.model?.id) {
      this.onSubmit();
      return;
    }
    if (!this.isCompanySectionValid()) {
      this.markControlsAsTouched([
        'companyName',
        'websiteUrl',
        'countryCode',
        'addressCity',
        'addressZip',
        'addressStreet',
        'addressState',
        'vatNumber',
        'departmentName',
      ]);
      return;
    }
    this.submitSection = 'company';
    this.onSubmit();
  }

  applyPlatformChanges() {
    if (!this.model?.id) {
      this.onSubmit();
      return;
    }
    if (!this.isPlatformSectionValid()) {
      this.markControlsAsTouched(['invoiceEmails', 'testTtl', 'emailForAlert', 'minBalance', 'password']);
      return;
    }
    this.submitSection = 'platform';
    this.onSubmit();
  }

  applyLegalContactsChanges() {
    if (!this.model?.id) {
      this.onSubmit();
      return;
    }
    if (!this.isLegalContactsSectionValid()) {
      this.markControlsAsTouched(['dataProtectionOfficerEmail', 'legalRepresentativeEmail', 'procurementEmail']);
      return;
    }
    this.submitSection = 'legalContacts';
    this.onSubmit();
  }

  applyInternalChanges() {
    if (!this.model?.id) {
      this.onSubmit();
      return;
    }
    if (!this.isInternalSectionValid()) {
      this.markControlsAsTouched(['parentId', 'source', 'rankingIndex', 'paymentType', 'comment']);
      return;
    }
    this.submitSection = 'internal';
    this.onSubmit();
  }

  onEmailBlur() {
    const email = this.model.email;
    const isEmailValid = this.form.get('email')?.valid;
    if (isEmailValid && !this.model.dataProtectionOfficerEmail) {
      this.model.dataProtectionOfficerEmail = email;
    }
  }

  private resetVatExemption(): void {
    this.model.vatExempt = false;
    this.form.get('vatExempt')?.setValue(false, { emitEvent: false });
  }

  onEnabledToggled() {
    if (!this.model?.id) {
      // In create flow, enabled is saved with the whole form as before
      return;
    }
    const newValue = !!this.model.enabled;
    const previous = this.savedModelSnapshot ? !!this.savedModelSnapshot.enabled : !newValue;
    this.loading = true;
    this.service.updateEnable(this.model.id, newValue).subscribe(
      () => {
        this.loading = false;
        if (this.savedModelSnapshot) {
          this.savedModelSnapshot.enabled = newValue;
        } else {
          this.captureSavedSnapshot();
        }
        this.notificationService.success('User updated', 'Users');
      },
      e => {
        this.loading = false;
        this.model.enabled = previous;
        this.form.get('enabled')?.setValue(previous, { emitEvent: false });
        this.notificationService.error({
          title: 'Users',
          message: 'An error occurred while updating the user',
          serviceName: 'API GATEWAY',
          requestMessage: e?.statusText,
          requestCode: e?.status,
          ts: e?.timestamp ? e.timestamp : null,
        });
      }
    );
  }
}
